import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, UnauthorizedError } from '../api';
import UnknownFilesAlert from '../components/UnknownFilesAlert';
import { Sparkline } from '../components/charts';
import FlowMap from '../components/FlowMap';
import { Icon, Pager, fmtNum, thaiDate, todayKey } from '../components/ui';

// คลิก Interchange → หน้าจัดการไฟล์กรองเฉพาะไฟล์ของ interchange นั้น (v2.0.4) · คลิก TUN → หน้าจัดการไฟล์ของ TUN นั้น
// v2.1.0: กราฟ 8 ชม./7 วัน แทนด้วยภาพ 3D การไหลของไฟล์ (FlowMap + /api/flow) · การ์ด "ผิดพลาด" เป็น "Unknow Error"
const OUTPUT_FOLDER = 'DATA/ORDERS/Output';
const SUMMARY_MS = 3000;
const FLOW_MS = 5000;
const CRON_MS = 15000;
const CHART_MS = 60000;
const PAGE_SIZE = 50;
const STALE_SEC = 180; // lock ไม่ถูกแตะเกิน 3 นาที = cron งานนั้นน่าจะหยุด (ปกติแตะทุกนาที)

const CRON_TEXT = { ok: 'ปกติ', error: 'ผิดพลาด', running: 'กำลังทำงาน', skip: 'ข้ามรอบ (รอบก่อนยังไม่เสร็จ)', unknown: 'ไม่ทราบสถานะ' };
const KIND = {
  int: { cls: 'c-int', label: 'Interface' },
  pdf: { cls: 'c-pdf', label: 'PDF' },
  hand: { cls: 'c-hand', label: 'ส่งต่อ PDF' },
};

function ageText(sec) {
  if (sec == null) return 'ไม่พบ';
  if (sec < 60) return `${Math.max(0, Math.round(sec))} วิ`;
  if (sec < 3600) return `${Math.round(sec / 60)} นาที`;
  if (sec < 86400) return `${Math.round(sec / 3600)} ชม.`;
  return `${Math.round(sec / 86400)} วัน`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [day, setDay] = useState(todayKey());
  const isToday = day === todayKey();
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState('');
  const [chart, setChart] = useState(null);
  const [flow, setFlow] = useState(null);
  const [cronJobs, setCronJobs] = useState(null);

  const [kind, setKind] = useState('all');
  const [tun, setTun] = useState('');
  const [errOnly, setErrOnly] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const onAuthFail = useCallback((e) => { if (e instanceof UnauthorizedError) navigate('/login'); }, [navigate]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await apiGet(`/api/summary?date=${day.replace(/-/g, '')}`);
      setSummary(data);
      setLoadError(false);
      setUpdatedAt(new Date().toLocaleTimeString('en-GB'));
    } catch (e) {
      onAuthFail(e);
      setLoadError(true);
    }
  }, [day, onAuthFail]);

  const loadChart = useCallback(async () => {
    try { setChart(await apiGet('/api/chart')); } catch (e) { onAuthFail(e); }
  }, [onAuthFail]);

  const loadFlow = useCallback(async () => {
    try { setFlow(await apiGet(`/api/flow?date=${day.replace(/-/g, '')}`)); } catch (e) { onAuthFail(e); }
  }, [day, onAuthFail]);

  const loadCron = useCallback(async () => {
    try { setCronJobs((await apiGet('/api/cron/health')).jobs || []); } catch (e) { onAuthFail(e); }
  }, [onAuthFail]);

  // วันนี้ = รีเฟรชตลอด · วันย้อนหลัง = โหลดครั้งเดียว (log ไม่เปลี่ยนแล้ว)
  // v2.2.9: หัวการ์ด + แถบกรองของตารางงานตรึงไว้ตอนเลื่อน — วัดความสูงให้หัวตารางต่อใต้พอดี
  const stickyRef = useRef(null);
  const [stickyH, setStickyH] = useState(0);
  useEffect(() => {
    const el = stickyRef.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setStickyH(el.offsetHeight));
    ro.observe(el); setStickyH(el.offsetHeight); return () => ro.disconnect();
  }, []);
  useEffect(() => {
    setSummary(null);
    loadSummary();
    if (!isToday) return undefined;
    const t = setInterval(loadSummary, SUMMARY_MS);
    return () => clearInterval(t);
  }, [loadSummary, isToday]);

  useEffect(() => {
    loadChart(); loadCron();
    const t2 = setInterval(loadCron, CRON_MS);
    const t3 = setInterval(loadChart, CHART_MS);
    return () => { clearInterval(t2); clearInterval(t3); };
  }, [loadChart, loadCron]);

  // ภาพ 3D: วันนี้รีเฟรชทุก 5 วิ (ไฟล์ใหม่ = กล่องวิ่ง) · วันย้อนหลังโหลดครั้งเดียว
  useEffect(() => {
    loadFlow();
    if (!isToday) return undefined;
    const t = setInterval(loadFlow, FLOW_MS);
    return () => clearInterval(t);
  }, [loadFlow, isToday]);

  useEffect(() => { setPage(0); }, [kind, tun, errOnly, q, day]);

  // รวม 3 ตารางเดิม (PDF-REPORT / Interface / รายผู้ส่ง) เป็นรายการเดียว แยกด้วยประเภท
  const rows = useMemo(() => {
    if (!summary) return [];
    const out = [];
    (summary.suppliers || []).forEach((s) => {
      const k = s.kind === 'handoff' ? 'hand' : 'int';
      out.push({
        kind: k, time: s.last_run || '', sender: s.sender, tun: s.tun,
        refs: s.interchange ? [s.interchange] : [],
        file: s.input_file && s.input_file !== '-' ? s.input_file : (s.output_file || ''),
        count: s.records || 0, ok: s.status === 'ok',
      });
    });
    ((summary.pdf && summary.pdf.senders) || []).forEach((s) => {
      out.push({
        kind: 'pdf', time: s.last_run || '', sender: s.sender, tun: s.tun,
        refs: s.interchanges || [], file: '', count: s.pdf_count || 0, ok: s.status === 'ok',
      });
    });
    return out.sort((a, b) => b.time.localeCompare(a.time));
  }, [summary]);

  const counts = useMemo(() => ({
    all: rows.length,
    int: rows.filter((r) => r.kind === 'int').length,
    pdf: rows.filter((r) => r.kind === 'pdf').length,
    hand: rows.filter((r) => r.kind === 'hand').length,
  }), [rows]);

  const tuns = useMemo(() => [...new Set(rows.map((r) => r.tun))].sort(), [rows]);
  // v2.0.1: แถว interface เป็น 1 แถวต่อไฟล์แล้ว — นับผู้ส่งจากชื่อที่ไม่ซ้ำ (แถว PDF ใช้ชื่อห้างชุดเดียวกัน)
  // v2.2.2: ไม่นับผู้ส่งที่หาไม่ได้ (ไฟล์ต้นทางหายและไม่มีในบันทึก) เป็นผู้ส่งอีกราย
  const senderCount = useMemo(() => new Set(rows.map((r) => r.sender).filter((s) => s && s !== 'ไม่ทราบ' && s !== '-')).size, [rows]);

  const filtered = rows.filter((r) => (kind === 'all' || r.kind === kind)
    && (!tun || r.tun === tun)
    && (!errOnly || !r.ok)
    && (!q || [r.sender, r.file, ...r.refs].some((v) => (v || '').toLowerCase().includes(q.toLowerCase()))));
  const pages = Math.ceil(filtered.length / PAGE_SIZE);
  const shown = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totals = (summary && summary.totals) || { total: 0, ok: 0, error: 0, records: 0 };
  const errRows = rows.filter((r) => !r.ok);
  const lastErr = errRows[0];
  const outInt = rows.filter((r) => r.kind === 'int' && r.ok).reduce((s, r) => s + r.count, 0);
  const outPdf = rows.filter((r) => r.kind === 'pdf' && r.ok).reduce((s, r) => s + r.count, 0);
  const outHand = counts.hand;
  const outAll = outInt + outPdf + outHand + errRows.length;

  const cron = (summary && summary.cron) || { status: 'unknown' };
  const staleJobs = (cronJobs || []).filter((j) => j.age == null || j.age > STALE_SEC);
  const unkToday = flow ? ((flow.unknown || {}).today || []).length : 0;
  const mailSent = flow ? ((flow.alerts || {}).sent || []).length : 0;
  // v2.1.4: Error ของ ConV2H ไม่อยู่ใน /api/summary — นับจาก /api/flow
  const convErr = flow ? (flow.errors || []).filter((e) => e.kind === 'conv').length : 0;

  // v2.0.4: คลิก Interchange → หน้าจัดการไฟล์กรองเฉพาะไฟล์ของ interchange นั้น (ต้นฉบับ + PDF + interface) ของวันที่ที่ดูอยู่
  //         คลิก TUN → หน้าจัดการไฟล์กรอง TUN นั้น (โฟลเดอร์ Output)
  const goInterchange = (t, ref) => navigate(`/files?tun=${encodeURIComponent(t)}&ic=${encodeURIComponent(ref)}&date=${day.replace(/-/g, '')}`);
  const goTun = (t) => navigate(`/files?tun=${encodeURIComponent(t)}&folder=${encodeURIComponent(OUTPUT_FOLDER)}`);

  const minDay = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); })();

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <h1>ภาพรวม</h1>
          <span className="meta">
            {thaiDate(day)} · cron {CRON_TEXT[cron.status] || cron.status}
            {cron.last_activity ? ` · ล่าสุด ${cron.last_activity}` : ''}
            {cron.exit_code != null && cron.exit_code !== 0 ? ` · exit ${cron.exit_code}` : ''}
          </span>
        </div>
        <div className="page-actions">
          <input type="date" className="input" style={{ width: 'auto' }} value={day} min={minDay} max={todayKey()}
            onChange={(e) => setDay(e.target.value || todayKey())} aria-label="เลือกวันที่" />
          {!isToday && <button className="btn btn-outline btn-sm" onClick={() => setDay(todayKey())}>วันนี้</button>}
          {isToday
            ? <span className={`live${loadError ? ' bad' : ''}`}><i />{loadError ? 'โหลดข้อมูลไม่สำเร็จ' : `อัปเดตอัตโนมัติ · ${updatedAt}`}</span>
            : <span className="live off"><i />ข้อมูลย้อนหลัง</span>}
        </div>
      </div>

      <UnknownFilesAlert />

      {staleJobs.length > 0 && (
        <section className="notice" style={{ borderLeftColor: 'var(--danger)' }}>
          <div className="notice-head">
            <div>
              <h3>งานอัตโนมัติ {staleJobs.length} งานไม่ได้ทำงานเกิน 3 นาที</h3>
              <p>{staleJobs.map((j) => j.label).join(', ')} · ตรวจ container pwsh-runner และ /home/APP/crontab.txt</p>
            </div>
          </div>
        </section>
      )}

      <section className="summary" aria-label="สรุปวันที่เลือก">
        <div className="summary-cell">
          <div>
            <div className="summary-label">ผู้ส่ง{isToday ? 'วันนี้' : ''}</div>
            <div className="summary-value">{summary ? fmtNum(senderCount) : '–'}</div>
            <div className="summary-sub">{summary ? `${fmtNum(totals.total)} ไฟล์ · ${tuns.length} TUN` : ' '}</div>
          </div>
          {chart && <Sparkline data={chart.ok.map((v, i) => v + chart.error[i])} />}
        </div>
        <div className="summary-cell">
          <div>
            <div className="summary-label">สำเร็จ</div>
            <div className="summary-value">{summary ? fmtNum(totals.ok) : '–'}</div>
            <div className="summary-sub ok">{summary ? `${fmtNum(totals.records)} รายการ` : ' '}</div>
          </div>
          {chart && <Sparkline data={chart.ok} color="var(--success)" />}
        </div>
        <div className="summary-cell">
          <div>
            <div className="summary-label">Unknow Error</div>
            <div className={`summary-value${errRows.length + unkToday + convErr ? ' bad' : ''}`}>{summary ? fmtNum(errRows.length + unkToday + convErr) : '–'}</div>
            <div className="summary-sub">{summary ? `Unknow ${fmtNum(unkToday)} · Error ${fmtNum(errRows.length + convErr)} · ✉ ${fmtNum(mailSent)} ฉบับ` : ' '}</div>
            {lastErr && <div className="summary-sub">Error ล่าสุด {lastErr.tun} · {lastErr.time}</div>}
          </div>
        </div>
        <div className="summary-cell wide">
          <div className="head"><span className="summary-label">ผลลัพธ์ที่ได้</span><span className="muted" style={{ fontSize: 12.5 }}>{fmtNum(outInt + outPdf)} ไฟล์/รายการ</span></div>
          <div className="stackbar">
            {outAll === 0 ? null : (
              <>
                {outInt > 0 && <span className="c-int" style={{ flex: outInt }} title={`Interface ${outInt}`} />}
                {outPdf > 0 && <span className="c-pdf" style={{ flex: outPdf }} title={`PDF ${outPdf}`} />}
                {outHand > 0 && <span className="c-hand" style={{ flex: outHand }} title={`ส่งต่อ PDF ${outHand}`} />}
                {errRows.length > 0 && <span className="c-err" style={{ flex: errRows.length }} title={`ผิดพลาด ${errRows.length}`} />}
              </>
            )}
          </div>
          <div className="legend">
            <span><i className="dot c-int" />Interface {fmtNum(outInt)}</span>
            <span><i className="dot c-pdf" />PDF {fmtNum(outPdf)}</span>
            <span><i className="dot c-hand" />ส่งต่อ PDF {fmtNum(outHand)}</span>
            <span><i className="dot c-err" />ผิดพลาด {fmtNum(errRows.length)}</span>
          </div>
        </div>
      </section>

      <FlowMap flow={flow} live={isToday} onTun={goTun} onInterchange={goInterchange} />

      <section className="card fm-list" style={{ '--fm-sticky-h': `${stickyH}px` }}>
        <div className="fm-sticky" ref={stickyRef}>
        <div className="card-head">
          <h3>งานที่ประมวลผล{isToday ? 'วันนี้' : ` · ${thaiDate(day)}`}</h3>
          <span className="card-note">รวม Interface, PDF-REPORT และการส่งต่อให้ PDF ไว้ในตารางเดียว</span>
        </div>
        <div className="toolbar">
          <div className="seg" role="group" aria-label="ประเภท">
            {[['all', 'ทั้งหมด'], ['int', 'Interface'], ['pdf', 'PDF'], ['hand', 'ส่งต่อ PDF']].map(([k, label]) => (
              <button key={k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{label}<span className="n">{counts[k]}</span></button>
            ))}
          </div>
          <select className="input" value={tun} onChange={(e) => setTun(e.target.value)} aria-label="กรองตาม TUN">
            <option value="">ทุก TUN</option>
            {tuns.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="check-label"><input type="checkbox" checked={errOnly} onChange={(e) => setErrOnly(e.target.checked)} />เฉพาะที่ผิดพลาด</label>
          <div className="search">
            <Icon name="search" />
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นหาผู้ส่ง, Interchange, ชื่อไฟล์" aria-label="ค้นหา" />
          </div>
        </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>เวลา</th><th>ผู้ส่ง</th><th>TUN</th><th>ประเภท</th><th>Interchange</th><th>ไฟล์</th><th className="r">ผลลัพธ์</th><th>สถานะ</th></tr>
            </thead>
            <tbody>
              {loadError && !summary ? (
                <tr><td colSpan={8} className="empty" style={{ color: 'var(--danger)' }}>โหลดข้อมูลไม่สำเร็จ ระบบจะลองใหม่อัตโนมัติ</td></tr>
              ) : !summary ? (
                <tr><td colSpan={8} className="empty">กำลังโหลด…</td></tr>
              ) : !shown.length ? (
                <tr><td colSpan={8} className="empty">{rows.length ? 'ไม่มีงานที่ตรงกับตัวกรอง' : 'ยังไม่มีงานในวันนี้'}</td></tr>
              ) : shown.map((r, i) => (
                <tr key={`${r.kind}-${r.tun}-${r.sender}-${r.time}-${i}`}>
                  <td className="num">{r.time}</td>
                  <td className="strong">{r.sender}</td>
                  <td><button className="code" title={`เปิดหน้าจัดการไฟล์ของ ${r.tun}`} onClick={() => goTun(r.tun)}>{r.tun}</button></td>
                  <td><span className="kind"><i className={`dot ${KIND[r.kind].cls}`} />{KIND[r.kind].label}</span></td>
                  <td>
                    <div className="codes">
                      {r.refs.map((ref, ri) => (
                        <button key={`${ref}-${ri}`} className="code" title={`ดูไฟล์ของ Interchange ${ref} ใน ${r.tun}`} onClick={() => goInterchange(r.tun, ref)}>{ref}</button>
                      ))}
                      {!r.refs.length && <span className="muted">—</span>}
                    </div>
                  </td>
                  <td>{r.file ? <span className="file" title={r.file}>{r.file}</span> : <span className="muted">—</span>}</td>
                  <td className="r num nowrap">
                    {!r.ok ? <span className="muted">—</span>
                      : r.kind === 'hand' ? <span className="muted">→ PDF-REPORT</span>
                        : <>{fmtNum(r.count)} <span className="muted">{r.kind === 'pdf' ? 'PDF' : 'รายการ'}</span></>}
                  </td>
                  <td>{r.ok ? <span className="st ok">● สำเร็จ</span> : <span className="st err">● ผิดพลาด</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="foot">
          <span>แสดง {filtered.length ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, filtered.length)}` : 0} จาก {filtered.length} รายการ</span>
          <Pager page={page} pages={pages} onPage={setPage} />
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h3>งานอัตโนมัติ (cron)</h3>
          <span className="card-note">
            {cronJobs ? `${cronJobs.length - staleJobs.length}/${cronJobs.length} งานทำงานปกติ · ` : ''}
            ดูจากเวลาที่ไฟล์ lock ถูกแตะล่าสุด ปกติทุก 1 นาที
          </span>
        </div>
        {!cronJobs ? <div className="card-body muted">กำลังโหลด…</div>
          : !cronJobs.length ? <div className="card-body muted">ไม่พบไฟล์ lock</div>
            : (
              <div className="health">
                {cronJobs.map((j) => {
                  const stale = j.age == null || j.age > STALE_SEC;
                  return (
                    <div key={j.name} title={j.name}>
                      <span className="name">{j.label}</span>
                      <span className={`age${stale ? ' stale' : ''}`}>{ageText(j.age)}</span>
                    </div>
                  );
                })}
              </div>
            )}
      </section>
    </>
  );
}
